import PropTypes from 'prop-types';

import classNames from 'classnames/bind';

import { useRef, useState } from 'react';

import styles from './BaseButton.module.scss';

const cx = classNames.bind(styles);

const noOp = () => {};

const BaseButton = ({
  id = null,
  onClick = noOp,
  color = 'primary',
  disabled = false,
  dataTest = null,
  className = null,
  contentClassName = '',
  roundedCorners = false,
  renderContent = null,
  renderIcon = null,
  renderTrailingIcon = null,
  dropShadow = false,
  fullWidth = false,
  size = 'medium',
  href = '',
  target = '',
  type = 'button',
  buttonRef: buttonRefProp = null,
  noEffectOnHover = false
}) => {
  const [ripples, setRipples] = useState([]);
  const innerButtonRef = useRef(null);
  const buttonRef = buttonRefProp || innerButtonRef;

  const btnClassName = cx(
    'base-button',
    `base-button--color-${color}`,
    `base-button--size-${size}`,
    {
      'base-button--rounded-corners': roundedCorners,
      'base-button--circular-button': !renderContent,
      'base-button--drop-shadow': dropShadow,
      'base-button--full-width': fullWidth,
      'base-button--no-hover': noEffectOnHover
    },
    className
  );

  const iconClassName = cx('base-button__icon');
  const trailingIconClassName = cx('base-button__trailing-icon');
  const textClassName = cx('base-button__text', contentClassName);

  const content = (
    <>
      {renderIcon && (
        <span className={iconClassName}>{renderIcon && renderIcon()}</span>
      )}
      {renderContent && (
        <span className={textClassName}>
          {renderContent && renderContent()}
        </span>
      )}
      {renderTrailingIcon && (
        <span className={trailingIconClassName}>
          {renderTrailingIcon && renderTrailingIcon()}
        </span>
      )}
    </>
  );

  const addRippleEffect = event => {
    if (disabled) {
      return;
    }

    const button = buttonRef.current;
    const rect = button.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const newRipple = {
      id: Date.now(),
      x,
      y
    };

    setRipples(prev => [...prev, newRipple]);

    // Remove ripple after animation
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, 600);
  };

  if (href) {
    return (
      <a
        id={id}
        href={href}
        target={target}
        onClick={onClick}
        data-test={dataTest}
        className={btnClassName}
        ref={buttonRef}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      id={id}
      // eslint-disable-next-line react/button-has-type
      type={type || 'button'} // This fallback is temporary, until we remove the Button wrapper and BaseButton becomes the only Button.
      onClick={onClick}
      onMouseDown={addRippleEffect}
      data-test={dataTest}
      disabled={disabled}
      className={btnClassName}
      ref={buttonRef}
    >
      {content}
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className={styles['base-button__ripple-effect']}
          style={{
            left: ripple.x,
            top: ripple.y
          }}
        />
      ))}
    </button>
  );
};

BaseButton.propTypes = {
  id: PropTypes.string,
  renderContent: PropTypes.func,
  renderIcon: PropTypes.func,
  renderTrailingIcon: PropTypes.func,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  roundedCorners: PropTypes.bool,
  onClick: PropTypes.func,
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'tertiary-light',
    'link'
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  dataTest: PropTypes.string,
  dropShadow: PropTypes.bool,
  href: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  target: PropTypes.oneOf(['_blank', '_self', '_parent', '_top', '']),
  buttonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]),
  noEffectOnHover: PropTypes.bool
};

export default BaseButton;
